/**
 * The register's catalog model, read once per page view and shared by the
 * terminal page's tabs (one in-flight read per modelId).
 *
 * `policy` is non-null ONLY for a model with a valid `cardSetupPolicy` — the
 * single gate every SUNMI card-setup path keys on. A failed read, a missing
 * modelId or a model without a policy all give `policy: null`, which means
 * "behave exactly as before".
 */
import { useEffect, useState } from 'react';
import { hardwareApi, type DeviceModelRow } from '../../../services/hardwareApi';
import { isCardSetupModel, policyOf, type CardSetupPolicy } from './cardSetupModel';

const inflight = new Map<string, Promise<DeviceModelRow | null>>();

export function readCatalogModel(modelId: string): Promise<DeviceModelRow | null> {
  let p = inflight.get(modelId);
  if (!p) {
    p = hardwareApi.getModel(modelId).then((r) => r?.model || null).catch(() => null);
    inflight.set(modelId, p);
    // Short-lived: a later page view re-reads (the model may have been edited).
    p.finally(() => setTimeout(() => inflight.delete(modelId), 5000));
  }
  return p;
}

export interface CatalogModelState {
  model: DeviceModelRow | null;
  policy: CardSetupPolicy | null;
  /** True until the read settles (or immediately false with no modelId). */
  loading: boolean;
}

export function useCatalogModel(modelId: string | null | undefined, refreshKey = 0): CatalogModelState {
  const [state, setState] = useState<CatalogModelState>({ model: null, policy: null, loading: !!modelId });
  useEffect(() => {
    let live = true;
    if (!modelId) { setState({ model: null, policy: null, loading: false }); return; }
    setState((s) => ({ ...s, loading: true }));
    if (refreshKey) inflight.delete(modelId);
    readCatalogModel(modelId).then((m) => {
      if (!live) return;
      setState({ model: m, policy: m && isCardSetupModel(m) ? policyOf(m) : null, loading: false });
    });
    return () => { live = false; };
  }, [modelId, refreshKey]);
  return state;
}

/** Tests only — forget every cached read. */
export const clearCatalogModelCache = () => inflight.clear();
